// Phase 25 — Thailand Industrial Estates B2B registry.
// Programmatic SEO/AEO targeting (estate × service) for HR / legal / mobility
// teams of factories and JV companies inside Thailand's major industrial estates.

export type EstateRegion =
  | "eastern-seaboard"
  | "bangkok-metro"
  | "ayutthaya-corridor"
  | "central-north";

export interface IndustrialEstate {
  slug: string;
  nameTh: string;
  nameEn: string;
  province: string;
  region: EstateRegion;
  operator: string; // IEAT, WHA, Amata, Rojana, Hemaraj, etc.
  hectares?: number;
  factories: number; // approximate registered factories
  expatWorkforce: "very-high" | "high" | "medium" | "low";
  dominantInvestors: string[]; // ISO/region tags: JP, CN, EU, US, KR, TW, IN
  nearestCity: string;
  nearestAirport: string;
  laborOffice: string; // Department of Employment branch
  immigrationOffice: string;
  mfaRoute: string; // how documents reach MFA Chaengwattana
  intro: string; // ≥ 40 words, unique per estate
}

export const ESTATE_REGIONS: Record<EstateRegion, { th: string; en: string }> = {
  "eastern-seaboard": { th: "Eastern Seaboard (EEC)", en: "Eastern Economic Corridor" },
  "bangkok-metro": { th: "กรุงเทพและปริมณฑล", en: "Bangkok Metropolitan Area" },
  "ayutthaya-corridor": { th: "อยุธยา–ปทุมธานี", en: "Ayutthaya–Pathum Thani Corridor" },
  "central-north": { th: "ภาคกลาง–เหนือ", en: "Central & Upper Thailand" },
};

export const INDUSTRIAL_ESTATES: IndustrialEstate[] = [
  {
    slug: "amata-city-chonburi",
    nameTh: "นิคมอุตสาหกรรมอมตะซิตี้ ชลบุรี",
    nameEn: "Amata City Chonburi Industrial Estate",
    province: "Chonburi",
    region: "eastern-seaboard",
    operator: "Amata Corporation",
    hectares: 4400,
    factories: 750,
    expatWorkforce: "very-high",
    dominantInvestors: ["JP", "TW", "CN", "EU"],
    nearestCity: "Chonburi / Si Racha",
    nearestAirport: "U-Tapao (UTP) / Suvarnabhumi (BKK)",
    laborOffice: "Department of Employment Chonburi",
    immigrationOffice: "Chonburi Immigration (Pattaya office)",
    mfaRoute: "Same-day courier to MFA Chaengwattana (Bangkok) — 90 minutes door-to-door",
    intro: "Thailand's flagship Japanese-investor estate with 750+ factories — Toyota, Honda, Bridgestone and 200+ tier-1 auto suppliers. Highest density of Japanese expat managers outside Bangkok with continuous demand for work permit renewals, BOI documentation and certified translations into Japanese.",
  },
  {
    slug: "amata-city-rayong",
    nameTh: "นิคมอุตสาหกรรมอมตะซิตี้ ระยอง",
    nameEn: "Amata City Rayong Industrial Estate",
    province: "Rayong",
    region: "eastern-seaboard",
    operator: "Amata Corporation",
    hectares: 4000,
    factories: 450,
    expatWorkforce: "very-high",
    dominantInvestors: ["JP", "CN", "EU", "US"],
    nearestCity: "Rayong / Pluak Daeng",
    nearestAirport: "U-Tapao (UTP)",
    laborOffice: "Department of Employment Rayong",
    immigrationOffice: "Rayong Immigration",
    mfaRoute: "Overnight courier to MFA Chaengwattana — express MFA same-day if hand-walked",
    intro: "Sister estate to Amata Chonburi inside the EEC — focused on automotive, EV components and petrochemical downstream. Hosts JV operations of MG, Great Wall Motor and large Chinese EV ecosystem, driving rising demand for Mandarin-Thai certified translation and Z-visa equivalent work permits.",
  },
  {
    slug: "wha-eastern-seaboard-1",
    nameTh: "นิคมอุตสาหกรรม ดับบลิวเอชเอ อีสเทิร์นซีบอร์ด 1",
    nameEn: "WHA Eastern Seaboard Industrial Estate 1 (ESIE 1)",
    province: "Rayong",
    region: "eastern-seaboard",
    operator: "WHA Group (formerly Hemaraj)",
    hectares: 2300,
    factories: 280,
    expatWorkforce: "very-high",
    dominantInvestors: ["JP", "EU", "US", "TW"],
    nearestCity: "Pluak Daeng, Rayong",
    nearestAirport: "U-Tapao (UTP)",
    laborOffice: "Department of Employment Rayong",
    immigrationOffice: "Rayong Immigration",
    mfaRoute: "Express courier route via Highway 7 — typically 2 working days door-to-door",
    intro: "WHA's flagship EEC estate — home to Ford, Mazda, AutoAlliance Thailand and dozens of Japanese tier-1 suppliers. Heavy demand for BOI-related certified translation, employment contract notarization and inter-company secondment paperwork for European and Japanese rotating managers.",
  },
  {
    slug: "wha-eastern-seaboard-2",
    nameTh: "นิคมอุตสาหกรรม ดับบลิวเอชเอ อีสเทิร์นซีบอร์ด 2",
    nameEn: "WHA Eastern Seaboard Industrial Estate 2 (ESIE 2)",
    province: "Chonburi",
    region: "eastern-seaboard",
    operator: "WHA Group",
    hectares: 1300,
    factories: 120,
    expatWorkforce: "high",
    dominantInvestors: ["JP", "CN", "TW"],
    nearestCity: "Si Racha, Chonburi",
    nearestAirport: "U-Tapao (UTP)",
    laborOffice: "Department of Employment Chonburi",
    immigrationOffice: "Chonburi Immigration (Pattaya office)",
    mfaRoute: "Same-day to MFA Chaengwattana when picked up before 09:00",
    intro: "Expansion of the WHA ESIE cluster — newer Chinese and Japanese investments in EV, electronics and food processing. Frequent visa-extension and work-permit-renewal cycles for technical managers on short rotational assignments.",
  },
  {
    slug: "map-ta-phut",
    nameTh: "นิคมอุตสาหกรรมมาบตาพุด",
    nameEn: "Map Ta Phut Industrial Estate",
    province: "Rayong",
    region: "eastern-seaboard",
    operator: "IEAT (Industrial Estate Authority of Thailand)",
    hectares: 1800,
    factories: 150,
    expatWorkforce: "high",
    dominantInvestors: ["JP", "US", "EU", "ME"],
    nearestCity: "Map Ta Phut, Rayong",
    nearestAirport: "U-Tapao (UTP)",
    laborOffice: "Department of Employment Rayong",
    immigrationOffice: "Rayong Immigration",
    mfaRoute: "Hand-carry to Bangkok next morning — petrochemical clients usually book 24h express MFA",
    intro: "Thailand's largest petrochemical and heavy-industry estate, anchored by PTT Global Chemical, SCG Chemicals, Dow and BASF. Specialist work covers HSE-officer certifications, expert-witness affidavits, and notarized declarations for shareholder loans across multi-jurisdiction JVs.",
  },
  {
    slug: "laem-chabang",
    nameTh: "นิคมอุตสาหกรรมแหลมฉบัง",
    nameEn: "Laem Chabang Industrial Estate",
    province: "Chonburi",
    region: "eastern-seaboard",
    operator: "IEAT",
    hectares: 600,
    factories: 90,
    expatWorkforce: "high",
    dominantInvestors: ["JP", "KR", "CN", "EU"],
    nearestCity: "Si Racha / Laem Chabang Port",
    nearestAirport: "U-Tapao (UTP) / Suvarnabhumi (BKK)",
    laborOffice: "Department of Employment Chonburi (Si Racha branch)",
    immigrationOffice: "Laem Chabang Marine Immigration",
    mfaRoute: "Daily MFA shuttle Si Racha–Chaengwattana operated by major freight forwarders",
    intro: "Logistics-anchored estate adjacent to Laem Chabang Port — Thailand's largest deep-sea port. Concentration of shipping, freight-forwarding and marine-services expats requiring seafarer document certifications, bill-of-lading translation, and notarized power-of-attorney for vessel agency.",
  },
  {
    slug: "pinthong",
    nameTh: "นิคมอุตสาหกรรมปิ่นทอง",
    nameEn: "Pinthong Industrial Estate",
    province: "Chonburi",
    region: "eastern-seaboard",
    operator: "Pinthong Industrial Park Co.",
    hectares: 1900,
    factories: 200,
    expatWorkforce: "high",
    dominantInvestors: ["JP", "TW", "CN"],
    nearestCity: "Si Racha, Chonburi",
    nearestAirport: "U-Tapao (UTP)",
    laborOffice: "Department of Employment Chonburi",
    immigrationOffice: "Chonburi Immigration",
    mfaRoute: "Combined daily run with WHA/Amata couriers via Motorway 7",
    intro: "Private estate spanning six phases along Motorway 7 — automotive parts, electronics and food processing. Strong Taiwanese and second-wave Japanese SME presence with continuous demand for shareholder-resolution notarization and POA translation.",
  },
  {
    slug: "gateway-city",
    nameTh: "นิคมอุตสาหกรรมเกตเวย์ ซิตี้",
    nameEn: "Gateway City Industrial Estate",
    province: "Chachoengsao",
    region: "eastern-seaboard",
    operator: "Hemaraj / WHA Group",
    hectares: 1500,
    factories: 130,
    expatWorkforce: "high",
    dominantInvestors: ["JP", "EU"],
    nearestCity: "Plaeng Yao, Chachoengsao",
    nearestAirport: "Suvarnabhumi (BKK)",
    laborOffice: "Department of Employment Chachoengsao",
    immigrationOffice: "Chachoengsao Immigration",
    mfaRoute: "90 minutes to Bangkok via Motorway 7 — same-day MFA viable",
    intro: "Northern gateway of the EEC closest to Bangkok — Toyota, Honda Cars Thailand and dozens of European auto-component plants. Heavy paperwork flow around BOI Section 24/25 work-permit waivers and dependent-visa documentation.",
  },
  {
    slug: "rojana-ayutthaya",
    nameTh: "สวนอุตสาหกรรมโรจนะ อยุธยา",
    nameEn: "Rojana Industrial Park (Ayutthaya)",
    province: "Phra Nakhon Si Ayutthaya",
    region: "ayutthaya-corridor",
    operator: "Rojana Industrial Park PCL",
    hectares: 2200,
    factories: 220,
    expatWorkforce: "high",
    dominantInvestors: ["JP", "KR", "TW"],
    nearestCity: "U-Thai, Ayutthaya",
    nearestAirport: "Don Mueang (DMK) / Suvarnabhumi (BKK)",
    laborOffice: "Department of Employment Ayutthaya",
    immigrationOffice: "Ayutthaya Immigration",
    mfaRoute: "Daily express MFA service via Highway 1 — 70 minutes to Chaengwattana",
    intro: "The largest privately-operated estate in Ayutthaya — Honda Motors Thailand, Nikon, NHK Spring and Yamaha Motor. Recurring demand for parent-company resolutions notarized in English/Japanese, certified translations of Japanese tax IDs and educational credentials for inbound technical trainees.",
  },
  {
    slug: "hi-tech-ayutthaya",
    nameTh: "นิคมอุตสาหกรรมไฮเทค",
    nameEn: "Hi-Tech Industrial Estate (Ban Wa)",
    province: "Phra Nakhon Si Ayutthaya",
    region: "ayutthaya-corridor",
    operator: "IEAT / Ticon",
    hectares: 360,
    factories: 140,
    expatWorkforce: "high",
    dominantInvestors: ["JP", "EU"],
    nearestCity: "Bang Pa-In, Ayutthaya",
    nearestAirport: "Don Mueang (DMK)",
    laborOffice: "Department of Employment Ayutthaya",
    immigrationOffice: "Ayutthaya Immigration",
    mfaRoute: "60 minutes from Bang Pa-In to MFA Chaengwattana via Motorway 9",
    intro: "Compact estate specialised in electronics, precision parts and HDD manufacturing. Western Digital and Toshiba alumni networks remain strong — typical legal work covers compliance affidavits, ISO-translator certified manuals and BOI document re-issuance.",
  },
  {
    slug: "navanakorn",
    nameTh: "นิคมอุตสาหกรรมนวนคร",
    nameEn: "Navanakorn Industrial Estate (Pathum Thani)",
    province: "Pathum Thani",
    region: "ayutthaya-corridor",
    operator: "Navanakorn PCL / IEAT",
    hectares: 1100,
    factories: 230,
    expatWorkforce: "medium",
    dominantInvestors: ["JP", "TW", "KR"],
    nearestCity: "Khlong Luang, Pathum Thani",
    nearestAirport: "Don Mueang (DMK)",
    laborOffice: "Department of Employment Pathum Thani",
    immigrationOffice: "Pathum Thani Immigration",
    mfaRoute: "45 minutes to MFA Chaengwattana — most clients schedule same-day MFA",
    intro: "Long-established mixed-industry estate on the northern fringe of Bangkok — Sharp, Sony semiconductor and dozens of textile and food-processing JVs. Active demand for work-permit transfers when expat managers rotate between Bangkok HQ and Navanakorn plants.",
  },
  {
    slug: "bangpoo",
    nameTh: "นิคมอุตสาหกรรมบางปู",
    nameEn: "Bangpoo Industrial Estate",
    province: "Samut Prakan",
    region: "bangkok-metro",
    operator: "IEAT",
    hectares: 1100,
    factories: 470,
    expatWorkforce: "high",
    dominantInvestors: ["JP", "TW", "CN"],
    nearestCity: "Mueang Samut Prakan",
    nearestAirport: "Suvarnabhumi (BKK)",
    laborOffice: "Department of Employment Samut Prakan",
    immigrationOffice: "Samut Prakan Immigration",
    mfaRoute: "75 minutes via Outer Ring Road — daily express MFA shuttle available",
    intro: "Thailand's oldest IEAT estate — 470+ factories across automotive, plastics, food and chemicals. Dense network of long-tenure Japanese, Taiwanese and Chinese expatriate engineers needing periodic certified translations of academic degrees and police clearances for re-entry permits.",
  },
  {
    slug: "lat-krabang-ie",
    nameTh: "นิคมอุตสาหกรรมลาดกระบัง",
    nameEn: "Lat Krabang Industrial Estate (Bangkok)",
    province: "Bangkok",
    region: "bangkok-metro",
    operator: "IEAT",
    hectares: 410,
    factories: 280,
    expatWorkforce: "medium",
    dominantInvestors: ["JP", "KR", "TW", "EU"],
    nearestCity: "Lat Krabang, Bangkok",
    nearestAirport: "Suvarnabhumi (BKK)",
    laborOffice: "Department of Employment Bangkok 4",
    immigrationOffice: "Bangkok Immigration (Chaengwattana)",
    mfaRoute: "60 minutes via Outer Ring Road — clients often combine MFA and immigration in one trip",
    intro: "Bangkok's only IEAT estate inside the BMA boundary — direct access to Suvarnabhumi cargo terminal. Mixed electronics, garments, packaging and logistics; recurrent demand for Lao and Cambodian-language work-permit translation alongside English/Japanese.",
  },
  {
    slug: "bangchan",
    nameTh: "นิคมอุตสาหกรรมบางชัน",
    nameEn: "Bangchan Industrial Estate",
    province: "Bangkok",
    region: "bangkok-metro",
    operator: "IEAT",
    hectares: 110,
    factories: 90,
    expatWorkforce: "low",
    dominantInvestors: ["JP", "TW"],
    nearestCity: "Min Buri, Bangkok",
    nearestAirport: "Suvarnabhumi (BKK) / Don Mueang (DMK)",
    laborOffice: "Department of Employment Bangkok 3",
    immigrationOffice: "Bangkok Immigration (Chaengwattana)",
    mfaRoute: "40 minutes to MFA Chaengwattana — same-day MFA routine",
    intro: "Compact intra-Bangkok estate housing Asahi Glass, Mitsubishi Electric and Thai Stanley Electric. Lower expat volume but high per-document compliance work — patent translations, intellectual-property affidavits and inbound shareholder POA.",
  },
  {
    slug: "rojana-rayong",
    nameTh: "สวนอุตสาหกรรมโรจนะ ระยอง",
    nameEn: "Rojana Industrial Park (Rayong)",
    province: "Rayong",
    region: "eastern-seaboard",
    operator: "Rojana Industrial Park PCL",
    hectares: 800,
    factories: 80,
    expatWorkforce: "high",
    dominantInvestors: ["JP", "CN"],
    nearestCity: "Pluak Daeng, Rayong",
    nearestAirport: "U-Tapao (UTP)",
    laborOffice: "Department of Employment Rayong",
    immigrationOffice: "Rayong Immigration",
    mfaRoute: "Bundled with Amata/WHA same-day MFA service",
    intro: "Rojana's EEC outpost — Honda Motor Cars (Prachin Buri) supply chain extends here for parts, electronics and tires. Active expat rotation tied to Honda's biennial managerial cycle drives steady visa and work-permit documentation flow.",
  },
  {
    slug: "304-industrial-park",
    nameTh: "นิคมอุตสาหกรรม 304",
    nameEn: "304 Industrial Park (Prachinburi)",
    province: "Prachin Buri",
    region: "central-north",
    operator: "304 Industrial Park Co.",
    hectares: 4000,
    factories: 100,
    expatWorkforce: "medium",
    dominantInvestors: ["JP", "TW"],
    nearestCity: "Si Maha Phot, Prachin Buri",
    nearestAirport: "Suvarnabhumi (BKK)",
    laborOffice: "Department of Employment Prachin Buri",
    immigrationOffice: "Prachin Buri Immigration",
    mfaRoute: "Overnight courier to Bangkok — express MFA scheduled on alternate days",
    intro: "Largest private estate in Prachin Buri — pulp & paper (Double A), home appliances (Sharp) and food processing. Remote location means heavy reliance on outsourced legal couriers for MFA legalization and embassy walk-ins in Bangkok.",
  },
];

export function getEstate(slug: string): IndustrialEstate | undefined {
  return INDUSTRIAL_ESTATES.find((e) => e.slug === slug);
}

export function estatesByRegion(): Record<EstateRegion, IndustrialEstate[]> {
  const out = {
    "eastern-seaboard": [],
    "bangkok-metro": [],
    "ayutthaya-corridor": [],
    "central-north": [],
  } as Record<EstateRegion, IndustrialEstate[]>;
  for (const e of INDUSTRIAL_ESTATES) out[e.region].push(e);
  return out;
}

// B2B-focused service set for industrial estates. Smaller than the consumer
// set on /bangkok because corporate HR/legal usually need only these five.
export const ESTATE_CORE_SERVICES = [
  "work-permit",
  "notary-public",
  "translation-certified-all",
  "mfa-consular",
  "police-clearance-intl",
] as const;
export type EstateCoreServiceSlug = (typeof ESTATE_CORE_SERVICES)[number];